/**
 * Werkbalken van het antwoordvak. Twee soorten:
 *
 * - `Werkbalk`: chat en WhatsApp, over een textarea met platte tekst.
 *   Alleen Lijst (`- `), Link (`tekst (url)`) en eventueel Afbeelding.
 * - `MailWerkbalk`: de mailstand, over de opmaakeditor (sessie 70,
 *   blok 3). Vet, cursief, onderstrepen, lijst, link en afbeelding, met
 *   de actieve stand zichtbaar (aria-pressed).
 */
import { useEditorState, type Editor } from "@tiptap/react";
import { useState, type ReactNode, type RefObject } from "react";

import { Icoon } from "../Icoon";
import { normaliseerAdres } from "./mailopmaak";

// ── Linkveld (gedeeld) ───────────────────────────────────────────────

function LinkVeld({
  url,
  onUrl,
  onInvoegen,
  onSluit,
}: {
  url: string;
  onUrl: (url: string) => void;
  onInvoegen: () => void;
  onSluit: () => void;
}) {
  return (
    <span className="linkveld">
      <input
        type="url"
        aria-label="Adres van de link"
        placeholder="https://"
        value={url}
        autoFocus
        onChange={(e) => onUrl(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onInvoegen();
          }
          if (e.key === "Escape") onSluit();
        }}
      />
      <button type="button" onClick={onInvoegen}>
        Invoegen
      </button>
    </span>
  );
}

// ── Werkbalk voor chat en WhatsApp (platte tekst) ────────────────────

/**
 * Alleen Lijst, Link en Afbeelding: chat en WhatsApp gaan als platte
 * tekst, dus geen vet, cursief of onderstrepen.
 */
export function Werkbalk({
  tekstRef,
  tekst,
  onTekst,
  onAfbeelding,
}: {
  tekstRef: RefObject<HTMLTextAreaElement>;
  tekst: string;
  onTekst: (tekst: string) => void;
  onAfbeelding?: () => void;
}) {
  const [link, setLink] = useState<{ start: number; eind: number } | null>(null);
  const [url, setUrl] = useState("");

  const selectie = () => {
    const el = tekstRef.current;
    const start = el?.selectionStart ?? tekst.length;
    const eind = el?.selectionEnd ?? start;
    return { start, eind };
  };

  const maakLijst = () => {
    const { start, eind } = selectie();
    const regelStart = tekst.lastIndexOf("\n", Math.max(0, start - 1)) + 1;
    const gevonden = tekst.indexOf("\n", eind);
    const regelEind = gevonden === -1 ? tekst.length : gevonden;
    const blok = tekst
      .slice(start === 0 ? 0 : regelStart, regelEind)
      .split("\n")
      .map((r) => (r.startsWith("- ") ? r : `- ${r}`))
      .join("\n");
    onTekst(tekst.slice(0, start === 0 ? 0 : regelStart) + blok + tekst.slice(regelEind));
  };

  const voegLinkIn = () => {
    if (!link) return;
    let adres = url.trim();
    if (!adres) return;
    if (!/^(https?:|mailto:)/i.test(adres)) adres = `https://${adres}`;
    const gekozen = tekst.slice(link.start, link.eind);
    const invoeg = gekozen ? `${gekozen} (${adres})` : adres;
    onTekst(tekst.slice(0, link.start) + invoeg + tekst.slice(link.eind));
    setLink(null);
    setUrl("");
  };

  return (
    <div className="werkbalk">
      <button type="button" title="Lijst" aria-label="Lijst" onClick={maakLijst}>
        <Icoon naam="lijstje" klein />
      </button>
      <button
        type="button"
        title="Link"
        aria-label="Link"
        onClick={() => setLink(link ? null : selectie())}
      >
        <Icoon naam="link" klein />
      </button>
      {onAfbeelding && (
        <button type="button" title="Afbeelding" aria-label="Afbeelding" onClick={onAfbeelding}>
          <Icoon naam="beeld" klein />
        </button>
      )}
      {link && (
        <LinkVeld url={url} onUrl={setUrl} onInvoegen={voegLinkIn} onSluit={() => setLink(null)} />
      )}
    </div>
  );
}

// ── Werkbalk voor de mailstand (opmaakeditor) ────────────────────────

function Knop({
  label,
  aan,
  open,
  onKlik,
  children,
}: {
  label: string;
  aan?: boolean;
  /** Een knop die een veld openklapt (Link): aria-expanded, geen aria-pressed. */
  open?: boolean;
  onKlik: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={aan === undefined ? undefined : aan}
      aria-expanded={open}
      className={aan ? "on" : undefined}
      // Muisklik houdt de focus (en de selectie) in de editor.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onKlik}
    >
      {children}
    </button>
  );
}

export function MailWerkbalk({
  editor,
  onAfbeelding,
}: {
  editor: Editor;
  onAfbeelding?: () => void;
}) {
  const stand = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      vet: e.isActive("bold"),
      cursief: e.isActive("italic"),
      onder: e.isActive("underline"),
      lijst: e.isActive("bulletList"),
      link: e.isActive("link"),
    }),
  });
  const [link, setLink] = useState<{ van: number; tot: number } | null>(null);
  const [url, setUrl] = useState("");

  const openLink = () => {
    if (link) {
      setLink(null);
      return;
    }
    const { from, to } = editor.state.selection;
    const bestaand = editor.getAttributes("link").href as string | undefined;
    setUrl(bestaand ?? "");
    setLink({ van: from, tot: to });
  };

  const sluitLink = () => {
    setLink(null);
    setUrl("");
    editor.commands.focus();
  };

  const voegLinkIn = () => {
    if (!link) return;
    const href = normaliseerAdres(url);
    const keten = editor.chain().focus().setTextSelection({ from: link.van, to: link.tot });
    if (!href) {
      // Leeg adres op een bestaande link: link weg, tekst blijft.
      if (editor.isActive("link")) keten.extendMarkRange("link").unsetLink().run();
      sluitLink();
      return;
    }
    if (link.van !== link.tot) {
      keten.setLink({ href }).run();
    } else if (editor.isActive("link")) {
      keten.extendMarkRange("link").setLink({ href }).run();
    } else {
      keten
        .insertContent({
          type: "text",
          text: href.replace(/^mailto:/i, ""),
          marks: [{ type: "link", attrs: { href } }],
        })
        .unsetMark("link")
        .run();
    }
    sluitLink();
  };

  return (
    <div className="werkbalk mailwerkbalk" role="group" aria-label="Opmaak">
      <Knop label="Vet" aan={stand.vet} onKlik={() => editor.chain().focus().toggleBold().run()}>
        <span className="letter vet">B</span>
      </Knop>
      <Knop
        label="Cursief"
        aan={stand.cursief}
        onKlik={() => editor.chain().focus().toggleItalic().run()}
      >
        <span className="letter cursief">I</span>
      </Knop>
      <Knop
        label="Onderstrepen"
        aan={stand.onder}
        onKlik={() => editor.chain().focus().toggleUnderline().run()}
      >
        <span className="letter onder">U</span>
      </Knop>
      <span className="scheiding" aria-hidden="true" />
      <Knop
        label="Lijst"
        aan={stand.lijst}
        onKlik={() => editor.chain().focus().toggleBulletList().run()}
      >
        <Icoon naam="lijstje" klein />
      </Knop>
      <Knop label="Link" aan={stand.link} open={link !== null} onKlik={openLink}>
        <Icoon naam="link" klein />
      </Knop>
      {onAfbeelding && (
        <Knop label="Afbeelding" onKlik={onAfbeelding}>
          <Icoon naam="beeld" klein />
        </Knop>
      )}
      {link && <LinkVeld url={url} onUrl={setUrl} onInvoegen={voegLinkIn} onSluit={sluitLink} />}
    </div>
  );
}
